import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Skeleton,
  FormControl,
  FormControlLabel,
  Switch,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { RiskLevel, RiskParticipant } from './useRiskReportController';
import { useRiskReportDialogController } from './useRiskReportDialogController';
import { getWeekStatus, WeekStatusKey } from '@utils/weekStatus';

interface RiskReportDialogProps {
  open: boolean;
  onClose: () => void;
}

const RISK_COLORS = {
  [RiskLevel.Clear]: { bg: '#ffffff', text: '#2e7d32', border: '#e0e0e0', label: 'Clear' },
  [RiskLevel.Yellow]: { bg: '#fff9c4', text: '#f57f17', border: '#fdd835', label: 'Yellow' },
  [RiskLevel.Orange]: { bg: '#ffe0b2', text: '#e65100', border: '#fb8c00', label: 'Orange' },
  [RiskLevel.Red]: { bg: '#ffcdd2', text: '#b71c1c', border: '#e53935', label: 'Red' },
} as const;

const WEEK_STATUS_COLORS: Record<WeekStatusKey, { bg: string; text: string; border: string }> = {
  success: { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  partial: { bg: '#fff8e1', text: '#f57f17', border: '#ffe082' },
  missed: { bg: '#fce4ec', text: '#c62828', border: '#ef9a9a' },
};

const headerSx = {
  fontWeight: 600,
  fontSize: '0.7rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: '#546e7a',
  borderBottom: '2px solid #e0e0e0',
  py: 1,
  whiteSpace: 'nowrap' as const,
};

export const RiskReportDialog: React.FC<RiskReportDialogProps> = ({ open, onClose }) => {
  const {
    data,
    isLoading,
    riskFilter,
    filteredCohorts,
    maxWeeks,
    showInactive,
    setShowInactive,
    inactiveCount,
    upcomingCount,
    cohortStatusFilter,
    handleFilterChange,
    handleCohortStatusFilterChange,
  } = useRiskReportDialogController(open);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          margin: '40px',
          width: 'calc(100vw - 80px)',
          height: 'calc(100vh - 80px)',
          maxWidth: 'none',
          maxHeight: 'none',
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #e0e0e0',
          background: '#fafafa',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238' }}>
              Risk Report
            </Typography>
            <Typography variant="body2" sx={{ color: '#546e7a', mt: 0.5 }}>
              Active and upcoming cohorts with participant risk assessment
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {inactiveCount > 0 && (
              <FormControlLabel
                control={
                  <Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} size="small" />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#546e7a' }}>
                    Show inactive ({inactiveCount})
                  </Typography>
                }
              />
            )}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Risk Filter</InputLabel>
              <Select
                value={riskFilter === 'all' ? 'all' : String(riskFilter)}
                label="Risk Filter"
                onChange={handleFilterChange}
              >
                <MenuItem value="all">All Participants</MenuItem>
                <MenuItem value={String(RiskLevel.Yellow)}>Yellow & Above</MenuItem>
                <MenuItem value={String(RiskLevel.Orange)}>Orange & Above</MenuItem>
                <MenuItem value={String(RiskLevel.Red)}>Red Only</MenuItem>
              </Select>
            </FormControl>
            {upcomingCount > 0 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Cohort Status</InputLabel>
                <Select value={cohortStatusFilter} label="Cohort Status" onChange={handleCohortStatusFilterChange}>
                  <MenuItem value="all">All Cohorts</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="upcoming">Upcoming ({upcomingCount})</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
          {[RiskLevel.Clear, RiskLevel.Yellow, RiskLevel.Orange, RiskLevel.Red].map((level) => (
            <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '3px',
                  backgroundColor: RISK_COLORS[level].bg,
                  border: `1px solid ${RISK_COLORS[level].border}`,
                }}
              />
              <Typography variant="caption" sx={{ color: '#78909c' }}>
                {RISK_COLORS[level].label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 3 }}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : filteredCohorts.length > 0 ? (
          <Table stickyHeader sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...headerSx,
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    backgroundColor: '#fafafa',
                    minWidth: 160,
                  }}
                >
                  Participant
                </TableCell>
                <TableCell sx={{ ...headerSx, backgroundColor: '#fafafa', minWidth: 100 }}>Phone</TableCell>
                <TableCell sx={{ ...headerSx, backgroundColor: '#fafafa', minWidth: 80 }} align="center">
                  Logged In
                </TableCell>
                <TableCell sx={{ ...headerSx, backgroundColor: '#fafafa', minWidth: 80 }} align="center">
                  GTKY Survey
                </TableCell>
                <TableCell sx={{ ...headerSx, backgroundColor: '#fafafa', minWidth: 80 }} align="center">
                  Active Goal
                </TableCell>
                <TableCell sx={{ ...headerSx, backgroundColor: '#fafafa', minWidth: 80 }} align="center">
                  Paid
                </TableCell>
                <TableCell sx={{ ...headerSx, backgroundColor: '#fafafa', minWidth: 120 }}>Transaction Notes</TableCell>
                {Array.from({ length: maxWeeks }, (_, i) => (
                  <TableCell key={i} align="center" sx={{ ...headerSx, backgroundColor: '#fafafa', minWidth: 70 }}>
                    Week {i + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCohorts.map((cohort) => (
                <React.Fragment key={cohort.cohortId}>
                  {/* Cohort header row */}
                  <TableRow>
                    <TableCell
                      colSpan={7 + maxWeeks}
                      sx={{
                        backgroundColor: '#eceff1',
                        py: 1.5,
                        px: 2,
                        position: 'sticky',
                        left: 0,
                        fontWeight: 600,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#263238' }}>
                          {cohort.cohortName}
                        </Typography>
                        {cohort.isUpcoming && (
                          <Chip
                            label="Upcoming"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1565c0',
                              border: '1px solid #90caf9',
                            }}
                          />
                        )}
                        <Chip
                          label={`Start: ${cohort.startDate}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={`Day ${cohort.cohortDayNumber}`}
                          size="small"
                          sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={`Week ${cohort.currentWeekIndexZero}`}
                          size="small"
                          sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                  {/* Participant rows */}
                  {cohort.participants.map((participant) => (
                    <ParticipantRow key={participant.cohortUserId} participant={participant} maxWeeks={maxWeeks} />
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {data ? 'No participants match the selected filter' : 'No active cohorts found'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', borderRadius: '8px' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ParticipantRowProps {
  participant: RiskParticipant;
  maxWeeks: number;
}

const ParticipantRow: React.FC<ParticipantRowProps> = ({ participant, maxWeeks }) => {
  const riskColor = RISK_COLORS[participant.riskLevel];
  const isInactive = !participant.isActive;
  const tooltipContent =
    participant.riskReasons.length > 0
      ? participant.riskReasons.map((r) => `• ${r.reason}`).join('\n')
      : 'No risk factors';

  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}>{tooltipContent}</Box>}
      arrow
      placement="top-start"
    >
      <TableRow
        sx={{
          backgroundColor: riskColor.bg,
          opacity: isInactive ? 0.5 : 1,
          '&:hover': { backgroundColor: participant.riskLevel === RiskLevel.Clear ? '#f5f5f5' : riskColor.bg },
          borderLeft: participant.riskLevel > RiskLevel.Clear ? `4px solid ${riskColor.border}` : 'none',
        }}
      >
        <TableCell
          sx={{
            position: 'sticky',
            left: 0,
            backgroundColor: riskColor.bg,
            zIndex: 1,
            borderRight: '1px solid #e0e0e0',
            py: 0.75,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#37474f', fontSize: '0.8rem' }}>
              {participant.name || participant.email}
            </Typography>
            {isInactive && (
              <Chip label="Inactive" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ py: 0.75, fontSize: '0.75rem' }}>{participant.phone || '—'}</TableCell>
        <TableCell align="center" sx={{ py: 0.75 }}>
          <BooleanChip value={participant.hasLoggedIn} />
        </TableCell>
        <TableCell align="center" sx={{ py: 0.75 }}>
          <BooleanChip value={participant.hasCompletedGettingToKnowYouSurvey} />
        </TableCell>
        <TableCell align="center" sx={{ py: 0.75 }}>
          <BooleanChip value={participant.hasActiveGoal} />
        </TableCell>
        <TableCell align="center" sx={{ py: 0.75 }}>
          <BooleanChip value={participant.pledgeReceived} />
        </TableCell>
        <TableCell sx={{ py: 0.75, fontSize: '0.75rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Tooltip title={participant.pledgeTransactionId || ''} arrow>
            <span>{participant.pledgeTransactionId || '—'}</span>
          </Tooltip>
        </TableCell>
        {Array.from({ length: maxWeeks }, (_, i) => {
          const week = participant.weeks[i];
          if (!week) {
            return (
              <TableCell key={i} align="center" sx={{ py: 0.75 }}>
                —
              </TableCell>
            );
          }
          const status = getWeekStatus(week.progressCount, week.goalCount);
          const colors = WEEK_STATUS_COLORS[status];
          return (
            <TableCell key={i} align="center" sx={{ p: 0.5 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: colors.bg,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  px: 1,
                  py: 0.25,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  minWidth: 40,
                }}
              >
                {week.progressCount}/{week.goalCount}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>
    </Tooltip>
  );
};

interface BooleanChipProps {
  value: boolean;
}

const BooleanChip: React.FC<BooleanChipProps> = ({ value }) => (
  <Chip
    label={value ? 'Yes' : 'No'}
    size="small"
    sx={{
      fontSize: '0.65rem',
      height: 20,
      fontWeight: 600,
      backgroundColor: value ? '#e8f5e9' : '#fce4ec',
      color: value ? '#2e7d32' : '#c62828',
      border: `1px solid ${value ? '#a5d6a7' : '#ef9a9a'}`,
    }}
  />
);
